/* eslint-disable import/prefer-default-export */
import { defineStore } from 'pinia';
import chargeService from '@/services/ChargeService';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import { chargesCache } from './useUniversalChargesStore';

// chargesCache is keyed only by matter and outlives route changes, so any mutation
// that changes which fees a matter's time-entry picker offers (lock, create,
// update, delete) clears it. Completion, standard charges and entry moves don't
// affect that list, so they leave it alone.

export const useChargesStore = defineStore('chargesStore', {
  state: () => {
    const {
      handleError
    } = useSnackbarHandler();
    return {
      handleError,
      standardCharges: [],
      charges: [],
      timeEntries: {},
      loading: false,
      error: null
    };
  },
  getters: {
    // Computed property for standard charges with display text (label or description)
    // Uses trim() to handle whitespace-only labels properly
    standardChargesWithDisplay: state => {
      return state.standardCharges.map(charge => ({
        ...charge,
        displayText: charge.label?.trim() || charge.description
      }));
    }
  },
  actions: {
    async loadStandardCharges(reload = false) {
      try {
        if (this.standardCharges.length && !reload) {
          return this.standardCharges;
        }
        const charges = await chargeService.loadStandardCharges();
        this.$patch({
          standardCharges: charges
        });
        return this.standardCharges;
      } catch (error) {
        this.handleError(error);
        return [];
      }
    },
    async loadChargesTable(params = {}) {
      this.loading = true;
      this.error = null;
      try {
        const response = await chargeService.getChargesTable(params);
        // API returns: { items: [...], count: number }
        if (response && typeof response === 'object') {
          this.charges = response.items || [];
          return {
            data: response,
            // Return full response with items and count
            page: params.page || 1,
            pageSize: params.pageSize || 50,
            totalCount: response.count || 0
          };
        }
        return {
          data: {
            items: [],
            count: 0
          },
          page: 1,
          pageSize: 50,
          totalCount: 0
        };
      } catch (err) {
        this.error = err;
        this.handleError(err);
        return {
          data: {
            items: [],
            count: 0
          },
          page: 1,
          pageSize: 50,
          totalCount: 0
        };
      } finally {
        this.loading = false;
      }
    },
    async exportChargesTable(params = {}) {
      this.error = null;
      try {
        return await chargeService.exportChargesTable(params);
      } catch (err) {
        this.error = err;
        this.handleError(err);
        throw err;
      }
    },
    clearCharges() {
      this.charges = [];
      this.error = null;
    },
    async listChargesForTimeEntry({
      matterId
    }, forceRefresh = false) {
      if (!matterId) return [];
      const currentParams = matterId;
      const cache = chargesCache.getEntryCache();
      try {
        if (cache.entryCharges.length && cache.lastEntryParams === currentParams && !forceRefresh) {
          return cache.entryCharges;
        }
        const entryCharges = await chargeService.listChargesForTimeEntry(matterId);
        chargesCache.setEntryCache(entryCharges, currentParams);
        return entryCharges;
      } catch (error) {
        this.handleError(error);
        return [];
      }
    },
    async createChargeWithType(chargeData, isStandardCharge) {
      if (isStandardCharge) {
        await this.createStandardCharge(chargeData);
        return this.loadStandardCharges(true);
      }
      return this.createCharge(chargeData);
    },
    async listCharges({
      matterId,
      billed,
      acceptsTime = false
    }, forceRefresh = false) {
      if (!matterId) return [];
      const currentParams = acceptsTime ? `${matterId}-acceptsTime` : `${matterId}-${billed}`;
      const cache = chargesCache.getCache();
      try {
        if (cache.fixedFeeList.length && cache.lastParams === currentParams && !forceRefresh) {
          return cache.fixedFeeList;
        }
        const charges = await chargeService.listCharges(matterId, billed, acceptsTime);
        chargesCache.setCache(charges, currentParams);
        return charges;
      } catch (error) {
        this.handleError(error);
        return [];
      }
    },
    // Methods that need state management can stay in the store
    async getCharge(chargeId) {
      this.loading = true;
      this.error = null;
      try {
        const data = await chargeService.getCharge(chargeId);
        return data;
      } catch (err) {
        this.error = err;
        this.handleError(err);
        throw err;
      } finally {
        this.loading = false;
      }
    },
    async createCharge(chargeData) {
      this.loading = true;
      this.error = null;
      try {
        const data = await chargeService.createCharge(chargeData);
        chargesCache.clearCache();
        return data;
      } catch (err) {
        this.error = err;
        this.handleError(err);
        throw err;
      } finally {
        this.loading = false;
      }
    },
    async createStandardCharge(chargeData) {
      this.loading = true;
      this.error = null;
      try {
        const data = await chargeService.createStandardCharge(chargeData);
        return data;
      } catch (err) {
        this.error = err;
        this.handleError(err);
        throw err;
      } finally {
        this.loading = false;
      }
    },
    async updateCharge(chargeId, chargeData) {
      this.loading = true;
      this.error = null;
      try {
        const data = await chargeService.updateCharge(chargeId, chargeData);
        chargesCache.clearCache();
        this.loadStandardCharges(true);
        return data;
      } catch (err) {
        this.error = err;
        this.handleError(err);
        throw err;
      } finally {
        this.loading = false;
      }
    },
    async deleteCharge(chargeId, force = false) {
      this.loading = true;
      this.error = null;
      try {
        const data = await chargeService.deleteCharge(chargeId, force);
        chargesCache.clearCache();
        return data;
      } catch (err) {
        this.error = err;
        this.handleError(err);
        throw err;
      } finally {
        this.loading = false;
      }
    },
    async getDeletionPreview(chargeIds) {
      this.loading = true;
      this.error = null;
      try {
        const data = await chargeService.getDeletionPreview(chargeIds);
        return data;
      } catch (err) {
        this.error = err;
        this.handleError(err);
        throw err;
      } finally {
        this.loading = false;
      }
    },
    async bulkDeleteCharges(chargeIds, force = false) {
      this.loading = true;
      this.error = null;
      try {
        const data = await chargeService.bulkDeleteCharges(chargeIds, force);
        chargesCache.clearCache();
        return data;
      } catch (err) {
        this.error = err;
        this.handleError(err);
        throw err;
      } finally {
        this.loading = false;
      }
    },
    async deleteRecurring(chargeId, mode, force = false) {
      this.loading = true;
      this.error = null;
      try {
        const data = await chargeService.deleteRecurring(chargeId, mode, force);
        chargesCache.clearCache();
        return data;
      } catch (err) {
        this.error = err;
        this.handleError(err);
        throw err;
      } finally {
        this.loading = false;
      }
    },
    async bulkUpdateCompletion(chargeIds, completionState) {
      this.loading = true;
      this.error = null;
      try {
        const data = await chargeService.bulkUpdateCompletion(chargeIds, completionState);
        return data;
      } catch (err) {
        this.error = err;
        this.handleError(err);
        throw err;
      } finally {
        this.loading = false;
      }
    },
    async bulkUpdateLockStatus(chargeIds, timeLocked, wipDisposition = undefined) {
      this.loading = true;
      this.error = null;
      try {
        const data = await chargeService.bulkUpdateLockStatus(chargeIds, timeLocked, wipDisposition);
        chargesCache.clearCache();
        return data;
      } catch (err) {
        this.error = err;
        this.handleError(err);
        throw err;
      } finally {
        this.loading = false;
      }
    },
    async loadChargeTimeEntries(chargeId, forceRefresh = false) {
      if (!chargeId) {
        return [];
      }

      // Check cache first
      if (this.timeEntries[chargeId] && !forceRefresh) {
        return this.timeEntries[chargeId];
      }
      this.loading = true;
      this.error = null;
      try {
        const entries = await chargeService.getChargeTimeEntries(chargeId);
        const result = entries || [];

        // Cache the result
        this.timeEntries[chargeId] = result;
        return result;
      } catch (err) {
        this.error = err;
        this.handleError(err);
        return [];
      } finally {
        this.loading = false;
      }
    },
    async bulkConvertFromFixedFee(entryIds, targetBillingType) {
      this.loading = true;
      this.error = null;
      try {
        const data = await chargeService.bulkConvertFromFixedFee(entryIds, targetBillingType);
        return data;
      } catch (err) {
        this.error = err;
        this.handleError(err);
        throw err;
      } finally {
        this.loading = false;
      }
    },
    async bulkMoveFixedFeeEntriesToMatter(entryIds, targetMatterId) {
      this.loading = true;
      this.error = null;
      try {
        const data = await chargeService.bulkMoveFixedFeeEntriesToMatter(entryIds, targetMatterId);
        return data;
      } catch (err) {
        this.error = err;
        this.handleError(err);
        throw err;
      } finally {
        this.loading = false;
      }
    }
  }
});